/**
 * Account Menu
 *
 * The user button at the bottom of the sidebar plus a Claude-style popup
 * that opens above it. Shows the account email and a list of options:
 *
 *   Settings      → the account profile dialog (SettingsModal)
 *   Upgrade plan  → the public /pricing page, same view everyone sees
 *   Get help      → send an enquiry to the JAI team (HelpModal)
 *   Log out       → confirm first, since it drops them back to the login page
 *
 * Self-contained: it owns its own open/close state, the trigger button,
 * the popup, and outside-click / Escape dismissal. (A plain full-screen
 * backdrop can't be used here because the sidebar has a CSS transform,
 * which traps position:fixed children inside the sidebar's width — the
 * dialogs work around this by rendering through ModalPortal.)
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import ModalPortal, { ModalFooter, GhostButton, SolidButton } from './ModalPortal'
import SettingsModal from './SettingsModal'
import HelpModal from './HelpModal'
import { IS_NATIVE_APP } from '../services/runtime'

// ── SVG icon set (currentColor) ───────────────────────────────────────────────

const icons = {
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  upgrade: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M5 3h14l3 5-10 13L2 8l3-5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M2 8h20M8 3l-2 5 6 13M16 3l2 5-6 13"
        stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.55" />
    </svg>
  ),
  help: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9.2 9.3a2.8 2.8 0 0 1 5.4 1c0 1.9-2.6 2.3-2.6 4"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="17" r="0.7" fill="currentColor" />
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  caret: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M4 10l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

// ── Menu row ───────────────────────────────────────────────────────────────────

function MenuItem({ icon, label, onClick, highlight = false }) {
  const baseColor = highlight ? JAI_COLORS.blue : JAI_COLORS.navy
  const baseBg = 'transparent'
  const hoverBg = JAI_COLORS.paper

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition text-left"
      style={{
        background: baseBg,
        color: baseColor,
        fontFamily: JAI_FONTS.tight,
        fontSize: 14,
        fontWeight: highlight ? 600 : 500,
        cursor: 'pointer',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg }}
      onMouseLeave={e => { e.currentTarget.style.background = baseBg }}
    >
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 18, opacity: highlight ? 1 : 0.7 }}>
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  )
}

// ── Log out confirmation ──────────────────────────────────────────────────────

/**
 * Logging out is easy to undo (just log back in), but it's easy to hit by
 * accident too — and it throws away whatever the user was in the middle of.
 * So it asks once rather than acting immediately.
 */
function LogoutConfirm({ onCancel, onConfirm, isGuest }) {
  const [busy, setBusy] = useState(false)

  const confirm = async () => {
    setBusy(true)
    await onConfirm()
  }

  // For a guest this is not "log out" at all — it's "throw this away". There's
  // no password and no email, so nothing can ever get them back into this
  // account. Say so in plain words rather than reusing copy that promises
  // their leagues will be waiting for them.
  return (
    <ModalPortal onClose={onCancel} closable={!busy} width={420} labelledBy="logout-title">
      <div style={{ padding: '26px 26px 22px' }}>
        <h2 id="logout-title" style={{ margin: '0 0 9px', fontFamily: JAI_FONTS.tight, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: JAI_COLORS.navy }}>
          {isGuest ? 'Leave your guest session?' : 'Log out of JAI?'}
        </h2>
        <p style={{ margin: 0, fontFamily: JAI_FONTS.tight, fontSize: 14, lineHeight: 1.6, color: JAI_COLORS.mute }}>
          {isGuest
            ? "You're browsing as a guest, so this session can't be signed back into. Any leagues, notes and chat history in it will be gone. Create a free account first if you want to keep them."
            : "You'll be signed out on this device and sent back to the login page. Your leagues and notes stay exactly as they are."}
        </p>
      </div>

      <ModalFooter>
        <GhostButton onClick={onCancel} disabled={busy}>
          {isGuest ? 'Stay here' : 'Stay logged in'}
        </GhostButton>
        <SolidButton onClick={confirm} loading={busy} tone="danger">
          {busy ? 'Leaving…' : isGuest ? 'Leave anyway' : 'Log out'}
        </SolidButton>
      </ModalFooter>
    </ModalPortal>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AccountMenu() {
  const { user, logout, isGuest } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  // Which dialog is showing: '' | 'settings' | 'help' | 'logout'
  // A link like /chat?settings=preferences (used in JAI emails) opens Settings
  // straight on that tab. Only known tab names are honoured.
  const [deepLink] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('settings')
      return v === 'preferences' ? v : ''
    } catch { return '' }
  })
  const [dialog, setDialog] = useState(deepLink ? 'settings' : '')
  useEffect(() => {
    if (!deepLink) return
    // Drop the parameter so a refresh doesn't pop the dialog open again.
    const url = new URL(window.location.href)
    url.searchParams.delete('settings')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
  }, [deepLink])

  // Opening a dialog always closes the popup behind it.
  const openDialog = (name) => { setOpen(false); setDialog(name) }

  // Identity shown on the button: first name + its initial.
  // Falls back to username/email if the account has no first name.
  const displayName = user?.firstName || user?.username || user?.email?.split('@')[0] || 'Account'
  const initial = displayName.charAt(0).toUpperCase()

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return
    const onPointer = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleLogout = async () => {
    await logout()
    setDialog('')
    // Navigate explicitly. Clearing the session used to be enough — the old
    // ProtectedRoute bounced them to /login on its own — but it now hands a
    // session-less visitor a fresh guest account instead, which would drop
    // someone who just logged out straight back into the app as a stranger.
    navigate('/login')
  }

  // Straight to the public pricing page — the same three plans everyone sees
  // on the landing page. Deliberately not tailored to the user's current
  // tier: one pricing view is simpler to reason about (and to keep correct)
  // than a per-plan variant, and seeing the full ladder is useful context.
  const handleUpgrade = () => {
    setOpen(false)
    navigate('/pricing')
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Popup — floats just above the trigger button */}
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'white',
            borderRadius: 14,
            border: `1px solid ${JAI_COLORS.hair}`,
            boxShadow: '0 12px 40px rgba(4,18,46,0.30)',
            overflow: 'hidden',
            zIndex: 50,
          }}
        >
          {/* Email header. A guest's stored address is an internal placeholder
              (guest-…@guest.jai.local) — showing it would look like a real
              account, which is the opposite of what this session is. */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
            <div
              className="truncate"
              style={{ fontFamily: JAI_FONTS.tight, fontSize: 12.5, fontWeight: 500, color: JAI_COLORS.mute }}
            >
              {isGuest ? 'Guest session — this browser only' : user?.email}
            </div>
          </div>

          {/* Options. A guest has nothing to upgrade yet, so the same slot
              offers the step that comes first. "Upgrade plan" points at the
              public Pricing page, which doesn't exist on native (Guideline
              3.1.1 — the app sells nothing), so that entry is simply absent
              there. "Create your free account" stays — signup is free, not a
              purchase. */}
          <div style={{ padding: 6 }}>
            <MenuItem icon={icons.settings} label="Settings" onClick={() => openDialog('settings')} />
            {isGuest ? (
              <MenuItem icon={icons.upgrade} label="Create your free account" highlight onClick={() => { setOpen(false); navigate('/signup') }} />
            ) : (
              !IS_NATIVE_APP && <MenuItem icon={icons.upgrade} label="Upgrade plan" highlight onClick={handleUpgrade} />
            )}
            <MenuItem icon={icons.help} label="Get help" onClick={() => openDialog('help')} />
          </div>

          {/* Leaving — asks for confirmation first. For a guest this is the
              way to reach the login page and sign into a real account. */}
          <div style={{ padding: 6, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
            <MenuItem icon={icons.logout} label={isGuest ? 'Sign in to an account' : 'Log out'} onClick={() => openDialog('logout')} />
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition text-sm"
        style={{ color: 'white', background: open ? 'rgba(255,255,255,0.07)' : 'transparent' }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.backgroundColor = 'transparent' }}
        title="Account"
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
          <span style={{ color: JAI_COLORS.paper, fontSize: 12, fontWeight: 700 }}>{initial}</span>
        </div>
        {/* Same 15px/600 and same off-white as the nav items above, so the whole
            rail reads as one scale in one colour rather than two near-misses. */}
        <span className="truncate flex-1 text-left" style={{ color: JAI_COLORS.paper, fontSize: 15, fontWeight: 600 }}>
          {displayName}
        </span>
        <span className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }}>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
            {icons.caret}
          </span>
        </span>
      </button>

      {/* Dialogs — these render at the top level of the page (see
          ModalPortal), so the sidebar's slide-in transform can't trap them. */}
      {dialog === 'settings' && <SettingsModal initialSection={deepLink || undefined} onClose={() => setDialog('')} />}
      {dialog === 'help' && <HelpModal onClose={() => setDialog('')} />}
      {dialog === 'logout' && (
        <LogoutConfirm onCancel={() => setDialog('')} onConfirm={handleLogout} isGuest={isGuest} />
      )}
    </div>
  )
}
